// =====================================================================
//  CAUSA JUDICIAL
//
//  Antecedentes del expediente que origina el remate, y el resumen de los
//  artículos que gobiernan el procedimiento.
//
//  Los artículos están escritos acá y no en la base de datos a propósito:
//  son el texto de la ley, no datos del caso. Si cambian, cambia por una
//  reforma legal y corresponde una revisión del código, no una edición
//  desde la interfaz.
// =====================================================================

import { useArticulos, useCausa, useComunas, useConcesiones } from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga, TituloCard } from '../componentes/ui'
import { Pagina } from '../App'
import { fecha, fmt, fmtUtm, num, plural } from '../dominio/formato'
import type { RolUsuario } from '../tipos/dominio'
import { ArticuloCard, EditarCausa } from './CausaEditar'

export function Causa({ rol }: { rol: RolUsuario }) {
  const causa = useCausa()
  const concesiones = useConcesiones()
  const comunas = useComunas()
  const articulos = useArticulos()
  const esMaster = rol === 'MASTER'

  if (causa.isError) return <ErrorCarga error={causa.error} />
  if (causa.isLoading || !causa.data) return <Cargando />

  const c = causa.data
  const todas = concesiones.data ?? []
  const conNomina = [...new Set(todas.map((x) => x.comuna))].sort()
  const sinNomina = (comunas.data ?? [])
    .filter((cm) => !conNomina.includes(cm.nombre))
    .map((cm) => cm.nombre)

  // Resumen por nómina, tal como llegaron de la Tesorería.
  const nominas = [...new Set(todas.map((x) => x.folio_nomina))].sort().map((folio) => {
    const filas = todas.filter((x) => x.folio_nomina === folio)
    return {
      folio,
      numero: filas[0]?.nomina_numero ?? '',
      comuna: filas[0]?.comuna ?? '',
      utmNomina: num(filas[0]?.valor_utm),
      cuantas: filas.length,
      deuda: filas.reduce((s, x) => s + num(x.monto_adeudado), 0),
      utm: filas.reduce((s, x) => s + num(x.utm_adeudadas), 0),
    }
  })

  return (
    <>
      <Pagina titulo="Causa judicial" sub="Antecedentes del expediente que origina el remate." />

      <div className="card" style={{ marginBottom: 14 }}>
        <dl className="dl" style={{ gridTemplateColumns: '200px 1fr', margin: 0 }}>
          <dt>Rol</dt>
          <dd>
            <b>{c.rol}</b>
          </dd>
          <dt>Tribunal</dt>
          <dd>{c.tribunal}</dd>
          <dt>Caratulado</dt>
          <dd>{c.caratulado}</dd>
          <dt>Materia</dt>
          <dd>{c.materia}</dd>
          <dt>Demandante</dt>
          <dd>{c.demandante ?? '—'}</dd>
          <dt>Abogada patrocinante</dt>
          <dd>{c.abogado_patrocinante ?? '—'}</dd>
          <dt>Ingreso de la demanda</dt>
          <dd>{fecha(c.fecha_ingreso)}</dd>
          <dt>Resolución que decreta el remate</dt>
          <dd>{fecha(c.fecha_resolucion)}</dd>
          <dt>Publicación en el Diario Oficial</dt>
          <dd>
            {c.fecha_publicacion ? (
              fecha(c.fecha_publicacion)
            ) : (
              <span style={{ color: 'var(--warn)' }}>pendiente — límite: 20 de septiembre de 2026</span>
            )}
          </dd>
          <dt>Fecha de remate</dt>
          <dd>
            <b>{fecha(c.fecha_remate)}, 12:00 h</b>
          </dd>
          <dt>Modalidad</dt>
          <dd>
            {c.modalidad === 'VIDEOCONFERENCIA' ? 'Videoconferencia' : 'Presencial'}
            {c.enlace_remate ? (
              <>
                {' — '}
                <a href={c.enlace_remate} target="_blank" rel="noreferrer">
                  {c.enlace_remate}
                </a>
              </>
            ) : null}
          </dd>
          <dt>Comunas comprendidas</dt>
          <dd>
            {conNomina.join(' · ')}
            {sinNomina.length ? (
              <div className="sub" style={{ marginTop: 4 }}>
                Sin nómina cargada: {sinNomina.join(' · ')}
              </div>
            ) : null}
          </dd>
        </dl>
      </div>

      {/* --------- Las nóminas tal como llegaron --------- */}
      <div className="card" style={{ marginBottom: 14 }}>
        <TituloCard>Nóminas remitidas por la Tesorería</TituloCard>
        <table>
          <thead>
            <tr>
              <th>Folio</th>
              <th>Número</th>
              <th>Comuna</th>
              <th className="num">Concesiones</th>
              <th className="num">Deuda</th>
              <th className="num">UTM</th>
              <th className="num">Valor UTM</th>
            </tr>
          </thead>
          <tbody>
            {nominas.map((n) => (
              <tr key={n.folio}>
                <td className="mono">{n.folio}</td>
                <td className="mono sub">{n.numero}</td>
                <td className="sub">{n.comuna}</td>
                <td className="num">{n.cuantas}</td>
                <td className="num">{fmt(n.deuda)}</td>
                <td className="num sub">{fmtUtm(n.utm)}</td>
                <td className="num sub">{fmt(n.utmNomina)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3}>
                <b>Total</b>
              </td>
              <td className="num">
                <b>{todas.length}</b>
              </td>
              <td className="num">
                <b>{fmt(todas.reduce((s, x) => s + num(x.monto_adeudado), 0))}</b>
              </td>
              <td className="num">
                <b>{fmtUtm(todas.reduce((s, x) => s + num(x.utm_adeudadas), 0))}</b>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <p className="sub" style={{ marginTop: 10 }}>
          El monto adeudado de cada fila es exactamente sus UTM por el valor de la nómina. Esa relación
          se verifica en las {plural(todas.length, 'fila', 'filas')} con ±$5 de redondeo, y es lo que
          permite confiar en la extracción de los PDF.
        </p>
      </div>

      {/* --------- Edición del expediente (máster) --------- */}
      {esMaster ? <EditarCausa causa={c} /> : null}

      {/* --------- Los artículos, desde la base --------- */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
        {(articulos.data ?? []).map((a) => (
          <ArticuloCard key={a.id} articulo={a} editable={esMaster} />
        ))}
      </div>

      <Alerta tono="info" estilo={{ marginTop: 14 }}>
        <b>No es un remate civil común.</b> El mínimo no es una tasación sino la deuda; el dueño puede
        retirar su concesión pagando el doble hasta el último momento; y se remata por lote, no por
        mina. Las cuatro reglas están aplicadas por la base de datos: el sistema rechaza registrar
        cifras que la ley no permite, aunque alguien las escriba directamente en SQL.
      </Alerta>
    </>
  )
}
